import { View, DimensionValue } from 'react-native'
import { useTranslation } from 'react-i18next'

import { VideoConfigVariant } from '@ca/common/types'
import VimeoVideo from '@/common/Video/VimeoVideo'
import useVideoControls from '@/common/Video/useVideoControls'
import Header from '@ui/elements/Header'
import { StepOptions } from '@order/steps/types'

import Placeholder from './Placeholder/Placeholder'
import Avatar from './Avatar/Avatar'
import Option from './Option'
import useRWD from '@ui/theme/useRWD'

type Props = {
  title?: string
  desc?: string
  options: StepOptions
  video?: VideoConfigVariant | null
}

const Col = ({ children }: { children?: React.ReactNode }) => (
  <View className="h-full w-full flex-1 items-center justify-center">
    {children}
  </View>
)

const Row = ({
  children,
  span = 1,
}: {
  children?: React.ReactNode
  span?: number
}) => {
  const height = `${(100 * span) / 3}%` satisfies DimensionValue

  return (
    <View
      className="w-full flex-1 flex-row items-center gap-8 p-4"
      style={{ height, minHeight: height, maxHeight: height }}
    >
      {children}
    </View>
  )
}

const getIndex = (index: number, length: number) => {
  if (length <= 4) return index
  if (length <= 5) return index + 1
  if (length <= 6) return index + 2
  return index
}

export default function VideoTemplate({ title, desc, options, video }: Props) {
  const { screen } = useRWD()
  const { t } = useTranslation(['ui'])
  // get option item based on row and column (including options.length)
  const i = (item: number) => options[getIndex(item - 1, options.length)]
  const optionLarge = options.length <= 2

  const { player, isPlaying, togglePlay, isOverlayVisible } =
    useVideoControls({
      autoplay: false,
      overlayTime: video?.overlayTime,
    })

  const showOverlay = !video || isOverlayVisible
  return (
    <>
      <Header
        title={title}
        desc={desc}
        button={
          video
            ? {
                label: t(`ui:player.${isPlaying ? 'skip' : 'play'}`),

                iconStart: isPlaying
                  ? 'player-skip-forward-filled'
                  : 'player-play-filled',
                onPress: togglePlay,
              }
            : undefined
        }
      />
      <View
        className="mt-4 h-full flex-1 p-8"
        style={{
          flex: 1,
          width: screen.width,
          height: screen.width,
        }}
      >
        <View className="border-hairline flex h-full w-full flex-1 overflow-hidden rounded-2xl border-black/0">
          {video && (
            <View className="absolute inset-0 h-full w-full rounded-2xl">
              <VimeoVideo {...video} player={player} />
            </View>
          )}
          {showOverlay && (
            <>
              {!isPlaying && <Placeholder />}
              <View className="flex h-full w-full flex-1 flex-col p-4">
                <Row>
                  {options.length < 6 && <Col>{!isPlaying && <Avatar />}</Col>}
                  {options.length > 4 && (
                    <Col>
                      <Option {...options[0]} />
                    </Col>
                  )}
                  {options.length > 5 && (
                    <Col>
                      <Option {...options[1]} />
                    </Col>
                  )}
                </Row>
                <Row span={optionLarge ? 2 : 1}>
                  {i(1) && (
                    <Col>
                      <Option large={optionLarge} {...i(1)} />
                    </Col>
                  )}
                  {i(2) && (
                    <Col>
                      <Option large={optionLarge} {...i(2)} />
                    </Col>
                  )}
                </Row>
                {options.length > 2 && (
                  <Row>
                    {i(3) && (
                      <Col>
                        <Option {...i(3)} />
                      </Col>
                    )}
                    {i(4) && (
                      <Col>
                        <Option {...i(4)} />
                      </Col>
                    )}
                  </Row>
                )}
              </View>
            </>
          )}
        </View>
      </View>
    </>
  )
}
